// RegenerateDialog.js
// Extracted from AIWorkforcePage.js for better maintainability

import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Textarea } from '../../../components/ui/textarea';
import { Label } from '../../../components/ui/label';
import { Shuffle, MessageSquare, RefreshCw } from 'lucide-react';

/**
 * Dialog for regenerating/creating variations of a deliverable
 * 
 * @param {Object} props
 * @param {boolean} props.open - Controls dialog visibility
 * @param {function} props.onOpenChange - Callback when dialog open state changes
 * @param {Object} props.deliverable - The deliverable to regenerate
 * @param {string} props.feedback - User feedback for regeneration
 * @param {function} props.onFeedbackChange - Callback when feedback changes
 * @param {boolean} props.regenerating - Whether regeneration is in progress
 * @param {function} props.onRegenerate - Callback when regenerate is clicked
 * @param {function} props.onCancel - Callback when cancel is clicked
 */
export const RegenerateDialog = ({
  open,
  onOpenChange,
  deliverable,
  feedback,
  onFeedbackChange,
  regenerating = false,
  onRegenerate,
  onCancel
}) => {
  const isImage = deliverable?.type === 'image';
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shuffle className="h-5 w-5 text-amber-500" />
            {isImage ? 'Generate Image Options' : 'Create Content Variations'}
          </DialogTitle>
          <DialogDescription>
            Generate 3 variations for your team to choose from. Add feedback to guide the AI.
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div>
            <Label className="flex items-center gap-2 mb-2">
              <MessageSquare className="h-4 w-4" />
              What should change? (Optional)
            </Label>
            <Textarea
              value={feedback}
              onChange={(e) => onFeedbackChange(e.target.value)}
              placeholder={isImage 
                ? "e.g., Make it more vibrant, add more contrast, show product from different angle..."
                : "e.g., Make it more concise, use formal tone, emphasize key benefits..."
              }
              rows={3}
              data-testid="regenerate-feedback"
            />
            <p className="text-xs text-muted-foreground mt-1">
              Leave blank to generate variations of the current output
            </p>
          </div>
          
          <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
            <p className="text-sm text-amber-800">
              <strong>3 options</strong> will be generated. You can then select your preferred version.
            </p>
          </div>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={onCancel} data-testid="regenerate-cancel">
            Cancel
          </Button>
          <Button
            disabled={regenerating}
            className="bg-amber-500 hover:bg-amber-600"
            onClick={onRegenerate}
            data-testid="regenerate-submit"
          >
            {regenerating ? (
              <>
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <Shuffle className="h-4 w-4 mr-2" />
                Generate 3 Options
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default RegenerateDialog;
